import React, { useState, useEffect } from 'react';
import { _T, C } from '../../shared/theme';
import { s_GC } from '../../shared/styles';
import { getSocialStats } from '../../shared/dbService';
import Avatar from '../../components/ui/Avatar';
import SenseMeterGauge from './SenseMeterGauge';

// ─── Skill dimension definitions ─────────────────────────────────────────────
const SKILLS = [
  { key: 0, icon: '💙', label: 'Empathie',    labelEN: 'Empathy',     color: '#5B9ED0', desc: 'Reageren op en aanmoedigen van anderen', descEN: 'Reacting to and encouraging others' },
  { key: 1, icon: '🗣️', label: 'Expressie',   labelEN: 'Expression',  color: '#7B6FD0', desc: 'Actief delen en communiceren',           descEN: 'Actively sharing and communicating' },
  { key: 2, icon: '🤝', label: 'Samenwerking',labelEN: 'Teamwork',    color: '#2A9E82', desc: 'Samen oefenen en spelen',                descEN: 'Exercising and playing together' },
  { key: 3, icon: '✨', label: 'Aanwezigheid', labelEN: 'Presence',   color: '#C8A84B', desc: 'Consistent sociaal actief zijn',         descEN: 'Consistently being socially active' },
  { key: 4, icon: '🌟', label: 'Leiderschap', labelEN: 'Leadership',  color: '#D4873A', desc: 'Initiëren en groepen leiden',            descEN: 'Initiating and leading groups' },
];

// Convert a raw skill score (0+) to a visual level (1–10) and progress
function scoreToLevel(raw) {
  // Thresholds per level: roughly exponential
  const thresholds = [0, 5, 15, 30, 55, 90, 140, 210, 300, 420, 600];
  let level = 1;
  for (let i = 1; i < thresholds.length; i++) {
    if (raw >= thresholds[i]) level = i;
    else break;
  }
  level = Math.min(level, 10);
  const floor = thresholds[level - 1] || 0;
  const ceil  = thresholds[level]   || thresholds[thresholds.length - 1];
  const progress = ceil > floor ? Math.min((raw - floor) / (ceil - floor), 1) : 1;
  return { level, progress };
}

// Overall social tier based on total skill points
function getTier(totalRaw) {
  if (totalRaw >= 500) return { label: 'Community Pillar', labelNL: 'Gemeenschapspilaar', color: '#C8A84B', icon: '🏛️' };
  if (totalRaw >= 300) return { label: 'Influencer',      labelNL: 'Influencer',           color: '#7B6FD0', icon: '⭐' };
  if (totalRaw >= 150) return { label: 'Connected',       labelNL: 'Verbonden',             color: '#2A9E82', icon: '🤝' };
  if (totalRaw >= 60)  return { label: 'Grower',          labelNL: 'Groeier',               color: '#5B9ED0', icon: '🌱' };
  return                      { label: 'Starter',         labelNL: 'Starter',               color: C.silver,  icon: '🌿' };
}

function SkillBar({ skill, value, nl, expanded }) {
  const { level, progress } = scoreToLevel(value);
  const pct = Math.round(progress * 100);

  return (
    <div style={{ marginBottom: expanded ? 16 : 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 16 }}>{skill.icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: C.white, fontWeight: 600 }}>
              {nl ? skill.label : skill.labelEN}
            </span>
            <span style={{ fontSize: 10, color: skill.color, fontWeight: 700, background: `${skill.color}18`, padding: '2px 8px', borderRadius: 10 }}>
              Lv {level}
            </span>
          </div>
          {expanded && (
            <div style={{ fontSize: 10, color: C.silver, marginTop: 1 }}>
              {nl ? skill.desc : skill.descEN}
            </div>
          )}
        </div>
      </div>
      <div style={{ height: 5, background: `${skill.color}18`, borderRadius: 4, overflow: 'hidden' }}>
        <div style={{
          height: '100%', width: `${pct}%`, borderRadius: 4,
          background: `linear-gradient(90deg, ${skill.color}88, ${skill.color})`,
          transition: 'width 0.8s ease',
        }}/>
      </div>
    </div>
  );
}

function SocialStatsCard({ userId, lang, allUsers = [], friends = [], compact = false, myPostCount = 0 }) {
  const nl = lang === 'nl';
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    getSocialStats(userId).then(data => {
      setStats(data);
      setLoading(false);
    });
  }, [userId]);

  if (loading) {
    return (
      <div style={s_GC({ padding: 16, marginBottom: 12 })}>
        <div style={{ color: C.silver, fontSize: 12, textAlign: 'center' }}>···</div>
      </div>
    );
  }

  // If no stats yet, show empty state
  const skills = stats?.skills || [0, 0, 0, 0, 0];
  const totalRaw = skills.reduce((a, b) => a + b, 0);
  const tier = getTier(totalRaw);
  const uniquePartners = stats?.uniquePartners || 0;
  const topConnections = stats?.topConnections || [];

  // Find top connected users from allUsers
  const topUsers = topConnections.map(tc => ({
    user: allUsers.find(u => u.id === tc.target_id),
    strength: tc.strength,
  })).filter(x => x.user);

  if (compact) {
    return (
      <div style={{ ...s_GC({ padding: '10px 14px', marginBottom: 10 }) }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <span style={{ fontSize: 18 }}>{tier.icon}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: tier.color, fontWeight: 700, letterSpacing: 1 }}>
              {nl ? tier.labelNL : tier.label}
            </div>
            <div style={{ fontSize: 10, color: C.silver }}>
              {uniquePartners} {nl ? 'sociale connecties' : 'social connections'}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {SKILLS.map(s => {
            const { level } = scoreToLevel(skills[s.key]);
            return (
              <div key={s.key} style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: 14 }}>{s.icon}</div>
                <div style={{ fontSize: 9, color: s.color, fontWeight: 700 }}>{level}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div style={s_GC({ padding: '14px 16px', marginBottom: 14 })}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div style={{
          width: 42, height: 42, borderRadius: 12,
          background: `${tier.color}18`, border: `1px solid ${tier.color}44`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
        }}>
          {tier.icon}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 2 }}>
            {'SOCIALE VAARDIGHEDEN'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontSize: 14, color: tier.color, fontWeight: 700 }}>
              {nl ? tier.labelNL : tier.label}
            </div>
            <div style={{ fontSize: 10, color: C.silver }}>
              · {uniquePartners} {nl ? 'connecties' : 'connections'}
            </div>
          </div>
        </div>
        <button
          onClick={() => setExpanded(e => !e)}
          style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 13, padding: 4 }}
        >
          {expanded ? '▲' : '▼'}
        </button>
      </div>

      {/* Skill bars */}
      {SKILLS.map(s => (
        <SkillBar key={s.key} skill={s} value={skills[s.key]} nl={nl} expanded={expanded} />
      ))}

      {/* Top connections */}
      {topUsers.length > 0 && (
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${_T.cardBorderBot}` }}>
          <div style={{ fontSize: 9, color: C.silver, letterSpacing: 2, fontWeight: 600, marginBottom: 8 }}>
            {nl ? 'STERKSTE BANDEN' : 'STRONGEST BONDS'}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {topUsers.map(({ user, strength }, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <Avatar user={user} size={34} profileImage={user.picture || null} />
                <div style={{ fontSize: 9, color: C.silver, textAlign: 'center', maxWidth: 40, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user.name.split(' ')[0]}
                </div>
                <div style={{ fontSize: 8, color: _T.accent }}>{Math.min(100, strength)}pts</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state nudge */}
      {totalRaw === 0 && (
        <div style={{ color: C.silver, fontSize: 12, lineHeight: 1.6, marginTop: 4, fontStyle: 'italic' }}>
          {nl
            ? 'Reageer op posts, doe groepsoefeningen en speel games samen om je sociale vaardigheden te ontwikkelen.'
            : 'React to posts, do group exercises and play games together to develop your social skills.'}
        </div>
      )}

      {/* Social Sense Score speedometer */}
      <SenseMeterGauge skills={skills} myPostCount={myPostCount} lang={lang} />
    </div>
  );
}

export default SocialStatsCard;
